import { Injectable } from '@angular/core';
import {
  ChartConfiguration,
  ChartDataset,
  ElementOptionsByType,
  PluginOptionsByType,
  ScaleChartOptions,
  TooltipOptions,
} from 'chart.js';
import { DeepPartial } from 'node_modules/chart.js/dist/types/utils';

import { TrialInsightsChartModel } from '../../models/trial-insights-chart.model';
import { TrialInsightsLegendOptions } from '../../models/trial-insights-legend.model';

export type TICPCChartLabel = number;
export type TICPCChartType = 'bar';

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsClinicalPatientCostChartService
  implements TrialInsightsChartModel<TICPCChartType, TICPCChartLabel>
{
  elementOptions = (): DeepPartial<ElementOptionsByType<TICPCChartType>> => {
    return {
      bar: {
        borderColor: '#bacad0',
      },
    };
  };

  tooltipOptions = (): DeepPartial<TooltipOptions<TICPCChartType>> => {
    return {};
  };

  pluginOptions = (): DeepPartial<PluginOptionsByType<TICPCChartType>> => {
    return {
      legend: {
        display: false,
      },
      tooltip: this.tooltipOptions(),
    };
  };

  xAxesOptions = (expectedEnrolled?: number): DeepPartial<ScaleChartOptions<TICPCChartType>> => {
    return {
      scales: {
        x1: {
          beginAtZero: true,
          grid: {
            color: '#bacad0',
            display: true,
            lineWidth: 0.75,
            z: 1,
          },
          max: expectedEnrolled || 100,
          ticks: {
            callback: (value, index, values) => {
              if (values.length <= 4) {
                return value;
              }

              const lastIndex = values.length - 1;
              const nextLastIndex = lastIndex - 1;

              if (index !== lastIndex && index !== nextLastIndex) {
                return value;
              }

              const lastValue = Number(values[lastIndex]);
              const nextLastValue = Number(values[nextLastIndex]);

              if (index === nextLastIndex) {
                if (lastValue - nextLastValue <= 25) {
                  return null;
                }
              }

              return value;
            },
            display: true,
          },
          title: {
            display: false,
          },
        },
      },
    };
  };

  yAxesOptions = (): DeepPartial<ScaleChartOptions<TICPCChartType>> => {
    return {
      scales: {
        y: {
          display: false,
        },
      },
    };
  };

  chartOptions = (expectedEnrolled?: number): ChartConfiguration<TICPCChartType>['options'] => {
    return {
      elements: this.elementOptions(),
      indexAxis: 'y',
      maintainAspectRatio: false,
      plugins: this.pluginOptions(),
      responsive: true,
      scales: {
        ...this.xAxesOptions(expectedEnrolled).scales,
        ...this.yAxesOptions().scales,
      },
    };
  };

  labelOptions = () => {
    return ['Patients Enrolled'];
  };

  createChart = (
    datasets?: ChartDataset<TICPCChartType>[],
    expectedEnrolled?: number
  ): ChartConfiguration<TICPCChartType> => {
    return {
      data: {
        datasets: datasets ? datasets : [],
        labels: this.labelOptions(),
      },
      options: this.chartOptions(expectedEnrolled),
      type: 'bar',
    };
  };

  createDatasets = (patientsEnrolled?: number): ChartDataset<TICPCChartType>[] => {
    return [
      {
        backgroundColor: ['#095b95'],
        barThickness: 55,
        data: patientsEnrolled ? [patientsEnrolled] : [],
        hoverBackgroundColor: ['#256ea2'],
        xAxisID: 'x1',
      },
    ];
  };

  createLegend = (patientsEnrolled?: TICPCChartLabel): TrialInsightsLegendOptions => {
    return {
      centered: false,
      color: '',
      compact: false,
      data: [
        {
          displayDivider: false,
          displayIcon: true,
          iconColor: '#095b95',
          title: 'Patients Enrolled',
          value: patientsEnrolled ? patientsEnrolled + '' : '',
          valueColor: '#095b95',
        },
      ],
      divider: false,
    };
  };
}
